/**
 * One runtime owner for the exact remote sources accepted by `next/image`.
 * Consumers may apply stricter domain rules (for example HTTPS-only), but may
 * never admit a remote source outside this publication.
 */
export const VOLERA_NEXT_IMAGE_REMOTE_PATTERNS = Object.freeze([
  Object.freeze({
    protocol: 'https',
    hostname: 'images.unsplash.com',
    port: '',
    pathname: '/**',
  }),
  Object.freeze({
    protocol: 'https',
    hostname: 'assets.example.com',
    port: '',
    pathname: '/**',
  }),
  Object.freeze({
    protocol: 'https',
    hostname: 'nbg1.your-objectstorage.com',
    port: '',
    pathname: '/**',
  }),
  Object.freeze({
    protocol: 'http',
    hostname: 'localhost',
    port: '8080',
    pathname: '/**',
  }),
  Object.freeze({
    protocol: 'http',
    hostname: '127.0.0.1',
    port: '8080',
    pathname: '/**',
  }),
]);

/**
 * Fails closed for malformed/non-canonical values and for pathname patterns
 * this small shared policy does not explicitly understand.
 *
 * @param {unknown} value
 * @returns {value is string}
 */
export function isApprovedNextImageRemoteSource(value) {
  if (typeof value !== 'string' || value.length > 2_000) return false;

  try {
    const url = new URL(value);
    if (
      url.username !== '' ||
      url.password !== '' ||
      url.hash !== '' ||
      url.href !== value
    ) {
      return false;
    }

    return VOLERA_NEXT_IMAGE_REMOTE_PATTERNS.some((pattern) => {
      const pathnameMatches =
        pattern.pathname === '/**' && url.pathname.startsWith('/');

      return (
        pathnameMatches &&
        url.protocol === `${pattern.protocol}:` &&
        url.hostname === pattern.hostname &&
        url.port === pattern.port
      );
    });
  } catch {
    return false;
  }
}
